/**
 * Helpers that bound the wait for Luminary readiness (playable, and with match-cut stand-ins
 * rehydrated), since awaiting either directly can hang forever on a lost WebGL context or a stalled load.
 */

import type Luminary from '@lumen5/luminary';
import type { OpaqueVideoData, RawVideoData } from '@lumen5/luminary';

/** Default readiness bound: long enough to cover a healthy load, short enough to surface a hang. */
export const CAN_PLAY_TIMEOUT_MS = 30_000;

export async function awaitWithTimeout(
    promise: Promise<void>,
    timeoutMs: number,
    timeoutMessage: string,
): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
        await Promise.race([
            promise,
            new Promise<never>((_resolve, reject) => {
                timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
            }),
        ]);
    } finally {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
    }
}

/** Rejects if playback readiness stalls, so a lost WebGL context can't wedge the caller forever. */
export async function awaitCanPlayWithTimeout(
    luminary: Luminary,
    timeoutMs: number = CAN_PLAY_TIMEOUT_MS,
): Promise<void> {
    await awaitWithTimeout(
        luminary.onCanPlay(),
        timeoutMs,
        'Luminary did not become playable before the timeout (WebGL context may have been lost)',
    );
}

/** Rejects if match-cut rehydration stalls; resolves immediately when the video has no match-cut. */
export async function awaitMatchCutsRehydratedWithTimeout(
    luminary: Luminary,
    timeoutMs: number = CAN_PLAY_TIMEOUT_MS,
): Promise<void> {
    await awaitWithTimeout(
        luminary.onMatchCutsRehydrated(),
        timeoutMs,
        'Match-cut rehydration did not settle before the timeout',
    );
}

export async function awaitLuminaryReady(
    luminary: Luminary,
    timeoutMs: number = CAN_PLAY_TIMEOUT_MS,
): Promise<void> {
    // One deadline across both waits so timeoutMs is the total bound, not per-stage.
    const deadline = Date.now() + timeoutMs;
    await awaitCanPlayWithTimeout(luminary, timeoutMs);
    await awaitMatchCutsRehydratedWithTimeout(luminary, Math.max(0, deadline - Date.now()));
}

export async function loadLuminaryAndWaitUntilReady(
    luminary: Luminary,
    videoData: RawVideoData | OpaqueVideoData,
): Promise<void> {
    luminary.load(videoData);
    await awaitLuminaryReady(luminary);
}
